import { Decimal } from "classes/DecimalClasses";

import { i18n } from "services/i18nService";

import { addYears, subYears } from "date-fns";

const SECONDS = 1000; // JavaScript delay values are in milliseconds
export const MINUTES = 60 * SECONDS;

// setting date select bounds to catch two-digit date mistakes
export const DATE_SELECT_MIN_DATE = subYears(new Date(), 100);
export const DATE_SELECT_MAX_DATE = addYears(new Date(), 100);

// time inverval to refetch data
export const FETCH_NOTIFICATIONS_INTERVAL = 5 * MINUTES;
export const SYNC_STATUS_INTERVAL_ACTIVE = 5 * SECONDS;
export const SYNC_STATUS_INTERVAL_PASSIVE = 30 * SECONDS;

// timeout
export const TIMEOUT_WARNING_TIME_DEFICIT = 1 * MINUTES;
export const LAST_ACTIVITY_KEY = "sos_last_activity_timestamp";
export const TIMEOUT_STORAGE_UPDATE_THROTTLE = 5 * SECONDS;

// plans
export const PLAN_COMPANION = 0;
export const PLAN_PLUS = 1;
export const PLAN_PRO = 2;

// SOS pay
export const SOS_PAY_CREDIT = "Credit";
export const SOS_PAY_ACH = "ACH";

// U+2003 is the Unicode character for "em space" (a wide space)
export const INDENTED_LIST_PADDING = "\u2003\u2003";

// email domain used for support privilege fetch check
export const SOS_EMAIL_DOMAIN = "@sosinventory.com";

// Intuit email not verified reponse string
export const EMAIL_NOT_VERIFIED_INTUIT_STRING = "Email Not Verified";
export const INTUIT_ACCOUNT_MANAGER_URL =
  "https://accounts.intuit.com/app/account-manager/security";
export const INTUIT_AUTH_URL = "https://appcenter.intuit.com/connect/oauth2";

export const UPS_AUTH_REDIRECT_URL = `${process.env.REACT_APP_V9_URL}/upsauth`;
export const INTUIT_AUTH_REDIRECT_URL = `${process.env.REACT_APP_V9_URL}/intuitauth`;
export const SHOPIFY_AUTH_REDIRECT_URL = `${process.env.REACT_APP_V9_URL}/shopifyauth`;
export const PAY_URL = "https://payment.sosinventory.com/Pay";

export const SHOPIFY_TEST_ACCOUNT_ID = 77214;
export const SOS_TEST_ACCOUNT_ID = 1201;

export const ITEM_QUICKLIST_FORM_TYPES = {
  PURCHASING: "purchasing",
  SALES: "sales",
  PRODUCTION: "manufacturing", // legacy term used by the back end
};

export const ITEM_QUICKLIST_CASES = {
  ...ITEM_QUICKLIST_FORM_TYPES, // FE and BE tracked item types
  SERIAL: "serial", // only items where serialTracked is true
  LOT: "lot", // only items where lotTracked is true
  ALL: "all", // all inventory items
  CATEGORY: "category", // only items with type "Category"
  ASSEMBLY_AND_KIT: "assembly and kit", // only items with type "Assembly" or "Item Group" ("kit" is the preferred term, which is why we use it for our constant here)
  PHYSICAL_INVENTORY: "physical inventory",
  NON_CATEGORY: "non category", // all items with type other than "Category"
};

// misc
export const FINISHED_GOODS = { id: 1, name: "Finished Goods" };
export const NO_REF_NUMBER_STRING = "[no number]";
export const NO_REF_NAME_STRING = "[no name]";
export const NO_LAST_NAME_STRING = "[no last name]";
export const MAX_CHARS_LINE_ITEM_DESCRIPTION = 4000;

// max list results
export const DEFAULT_LIST_MAX_RESULTS = 25;
export const LIST_MAX_RESULTS_OPTIONS = [10, 25, 50, 100, 200];
export const LIST_MAX_MAX_RESULTS = Math.max(...LIST_MAX_RESULTS_OPTIONS);

export const MIN_SUPPORTED_PAGE_SIZE = "1280px";
export const TOP_NAV_BAR_HEIGHT = "50px";
export const CONFIGURATION_BAR_HEIGHT = "70px";

export const CONFIGURATION_PANEL1_WIDTH = "160px";
export const CONFIGURATION_PANEL_2_WIDTH = "160px";

export const LIST_TABLE_HEIGHT_OFFSET = 18;
export const CONFIGURATION_LIST_TABLE_OFFSET = 25;
export const NOTIFICATION_TABLE_HEIGHT_OFFSET = 16;
export const SYNC_TABLE_HEIGHT_OFFSET = 4;

// Fullsteam payment script urls
export const MERCHANT_TRACK_PROD_URL =
  "https://merchanttrack.fullsteampay.net/Account/SingleSignOn";
export const MERCHANT_TRACK_TEST_URL =
  "https://merchanttrack-ext.fullsteampay.net/Account/SingleSignOn";
export const FULLSTEAM_PROD_SCRIPT_URL =
  "https://hostedpayments.fullsteampay.net/js/hostedforms/1.0.0/fullsteam.hostedforms.js";
export const FULLSTEAM_TEST_SCRIPT_URL =
  "https://hostedpayments-ext.fullsteampay.net/js/hostedforms/1.0.0/fullsteam.hostedforms.js";
export const STRIPE_SCRIPT_URL = "https://js.stripe.com/v3/";
export const MAIN_SOS_TEST_ACCOUNT_ID = 1201;

export const SHIPSTATION_XML_LINK =
  "https://shipstation.sosinventory.com/ShipStation/Xml";

// user privileges
export const USER_PRIVILEGE = {
  activityLog: "ActivityLog",
  adjustInventory: "AdjustInventory",
  approvePos: "ApprovePOs",
  admin: "admin",
  buildAssemblies: "BuildAssemblies",
  canChangePricingOnSales: "CCP", // Abbreviated due to v8 cookie issue
  editCustomers: "EditCustomers",
  editEstimates: "EditEstimates",
  editInvoices: "EInv",
  editItems: "EditItems",
  editJobs: "EditJobs",
  editPurchaseOrders: "EditPOs",
  editRentals: "EditRentals",
  editSalesOrders: "EditSalesOrders",
  editSalesReceipts: "EditSRs",
  editSalesReps: "EditSalesReps",
  editShipments: "EditShipments",
  editShippingMethods: "EditShippingMethods",
  editVendors: "EditVendors",
  editWorkers: "EditWorkers",
  editWorkOrders: "EditWOs",
  inventorySettings: "InventorySettings",
  qboAccounts: "QBAccts",
  qboBills: "QBBills",
  qboBillPayments: "QBBP",
  qboChecks: "QBChecks",
  qboCharges: "QBCCCharges",
  qboEmployees: "QBEmpl",
  qboPaymentsReceived: "QBPR",
  qboCashPurchases: "QBCP",
  qboJournalEntries: "QBJournalEntries",
  qboSalesReceipts: "QBSalesReceipts",
  processPayment: "PrPa",
  purchasingSettings: "PurchasingSettings",
  receiveInventory: "ReceiveInventory",
  salesSettings: "SalesSettings",
  sosSupport: "SosSupport",
  sync: "Sync",
  sysAdmin: "sysAdmin",
  transferInventory: "TransferInventory",
  viewCosts: "ViewCosts",
  viewCustomers: "ViewCustomers",
  viewEstimates: "ViewEstimates",
  viewInventory: "ViewInventory",
  viewInvoices: "VInv",
  viewItems: "ViewItems",
  viewJobs: "ViewJobs",
  viewPurchaseOrders: "ViewPOs",
  viewRentals: "ViewRentals",
  viewSalesOrders: "ViewSalesOrders",
  viewSalesReceipts: "ViewSRs",
  viewShipments: "ViewShipments",
  viewVendors: "ViewVendors",
  viewWorkers: "ViewWorkers",
  viewWorkOrders: "ViewWOs",
};

// merge with USER_PRIVILEGE once https://app.clickup.com/t/868ajpcb2 complete
export const USER_EDIT_PRIVILEGES = {
  activityLog: "activityLog",
  adjustInventory: "adjustInventory",
  approvePurchaseOrders: "approvePurchaseOrders",
  admin: "admin",
  buildAssemblies: "buildAssemblies",
  changePricingOnSales: "changePricingOnSales",
  editCustomers: "editCustomers",
  editEstimates: "editEstimates",
  editInvoices: "editInvoices",
  editItems: "editItems",
  editJobs: "editJobs",
  editPurchaseOrders: "editPurchaseOrders",
  editRentals: "editRentals",
  editSalesOrders: "editSalesOrders",
  editSalesReceipts: "editSalesReceipts",
  editSalesReps: "editSalesReps",
  editShipments: "editShipments",
  editShippingMethods: "editShippingMethods",
  editVendors: "editVendors",
  editWorkers: "editWorkers",
  editWorkOrders: "editWorkOrders",
  inventorySettings: "inventorySettings",
  qboAccounts: "qboAccounts",
  qboBills: "qboBills",
  qboBillPayments: "qboBillPayments",
  qboChecks: "qboChecks",
  qboCharges: "qboCharges",
  qboEmployees: "qboEmployees",
  qboPaymentsReceived: "qboPaymentsReceived",
  qboCashPurchases: "qboCashPurchases",
  qboJournalEntries: "qboJournalEntries",
  qboSalesReceipts: "qboSalesReceipts",
  processPayments: "processPayments",
  purchasingSettings: "purchasingSettings",
  receiveInventory: "receiveInventory",
  salesSettings: "salesSettings",
  sosSupport: "sosSupport",
  sync: "sync",
  sysAdmin: "sysAdmin",
  transferInventory: "transferInventory",
  viewCosts: "viewCosts",
  viewCustomers: "viewCustomers",
  viewEstimates: "viewEstimates",
  viewInventory: "viewInventory",
  viewInvoices: "viewInvoices",
  viewItems: "viewItems",
  viewJobs: "viewJobs",
  viewPurchaseOrders: "viewPurchaseOrders",
  viewRentals: "viewRentals",
  viewSalesOrders: "viewSalesOrders",
  viewSalesReceipts: "viewSalesReceipts",
  viewShipments: "viewShipments",
  viewVendors: "viewVendors",
  viewWorkers: "viewWorkers",
  viewWorkOrders: "viewWorkOrders",
};

const privilegeArray = Object.keys(USER_PRIVILEGE).map((key) => [
  USER_PRIVILEGE[key],
  key,
]);
export const PRIVILEGE_STRING_TO_PROPERTY = Object.fromEntries(privilegeArray);

export const OBJECT_TYPES = {
  ACCOUNT: { fullString: "account", typeString: "account" },
  AUDIT_EVENT: { fullString: "auditevent" },
  ADJUSTMENT: {
    fullString: "adjustment",
    shortString: "",
    typeString: "inventory adjustment",
    emailBody: "",
    nextRefNumberSetString: "adjustmentHasNextRefNumberSet",
  },
  ALERT: { fullString: "alert" },
  AOP_RULE: { fullString: "aoprule" },
  API_CONNECTION: { fullString: "apiconnection" },
  BILL: { fullString: "bill", typeString: "bill" },
  BILL_PAYMENT: { fullString: "billpayment", typeString: "bill payment" },
  BIN: { fullString: "bins", typeString: "bin" },
  BOM: { fullString: "bom", typeString: "bom" },
  CART: { fullString: "cart", typeString: "cart" },
  COMMENT: { fullString: "comment" },
  BUILD: {
    fullString: "build",
    shortString: "",
    typeString: "build",
    emailBody: "",
    reportLink: "BuildAssembly",
    nextRefNumberSetString: "buildHasNextRefNumberSet",
  },
  CASH_PURCHASE: { typeString: "cash purchase", fullString: "cashpurchase" },
  CHECK: { typeString: "check", fullString: "check" },
  CHANNEL: { fullString: "channel", reportLink: "Channel" },
  CLASS: { fullString: "class", typeString: "class", reportLink: "Class" },
  CREDIT_CARD_CHARGE: {
    fullString: "creditcardcharge",
    typeString: "credit card charge",
  },
  CREDIT_CARD_CREDIT: {
    fullString: "creditcardcredit",
    typeString: "credit card credit",
  },
  CREDIT_MEMO: {
    fullString: "creditmemo",
    typeString: "credit memo",
    nextRefNumberSetString: "creditmemoHasNextRefNumberSet",
    noEditModal: true,
  },
  CUSTOMER: {
    fullString: "customer",
    shortString: "",
    typeString: "customer",
    emailBody: "",
    reportLink: "Customer",
  },
  CUSTOM_FIELD: { fullString: "customfield" },
  CUSTOMER_TYPE: {
    fullString: "customertype",
    reportLink: "CustomerType",
    typeString: "customer type",
  },
  CUSTOMER_MESSAGE: { fullString: "customermessage" },
  DEPARTMENT: {
    fullString: "department",
    typeString: "department",
    reportLink: "Department",
  },
  DOCUMENT: { fullString: "document" },
  DELETED_ITEM: { fullString: "deleteditem" },
  EMPLOYEE: { fullString: "employee", typeString: "employee" },
  ESTIMATE: {
    fullString: "estimate",
    shortString: "",
    typeString: "estimate",
    emailBody: "emailBodyEstimates",
    reportLink: "Estimate",
    titleString: "estimateTitle",
    nextRefNumberSetString: "estimateHasNextRefNumberSet",
  },
  FORM_TEMPLATE: { fullString: "formtemplate" },
  FTP_CONNECTION: { fullString: "ftpconnection" },
  FS_PAYMENT: { fullString: "fspayment" },
  INVOICE: {
    fullString: "invoice",
    shortString: "inv",
    // needed due to BE sending inconsistent linkedTransaction
    // transactionType values on payment and shipment records
    linkedTransactionString: "Invoice",
    typeString: "invoice",
    emailBody: "emailBodyInvoices",
    reportLink: "Invoice",
    nextRefNumberSetString: "invoiceHasNextRefNumberSet",
  },
  ITEM: {
    fullString: "item",
    shortString: "",
    typeString: "item",
    emailBody: "",
    reportLink: "Item",
  },
  ITEM_RECEIPT: {
    fullString: "itemreceipt",
    shortString: "IR",
    typeString: "item receipt",
    emailBody: "",
    reportLink: "ItemReceipt",
    nextRefNumberSetString: "itemreceiptHasNextRefNumberSet",
  },
  JOB: {
    fullString: "job",
    shortString: "",
    typeString: "job",
    emailBody: "",
    reportLink: "Job",
    nextRefNumberSetString: "jobHasNextRefNumberSet",
  },
  JOURNAL_ENTRY: { fullString: "journalentry", typeString: "journal entry" },
  LOCATION: {
    fullString: "location",
    typeString: "location",
    reportLink: "Location",
  },
  LOT: {
    fullString: "lot",
    shortString: "",
    typeString: "lot",
    emailBody: "",
    reportLink: "Lot",
  },
  PAYMENT: {
    fullString: "payment",
    typeString: "payment",
    shortString: "payment",
    nextRefNumberSetString: "paymentHasNextRefNumberSet",
  },
  ORDER_STAGE: { fullString: "orderstage" },
  PAYMENT_METHOD: { fullString: "paymentmethod", typeString: "payment method" },
  PICK_TICKET: {
    fullString: "pickticket",
    shortString: "pick ticket",
    typeString: "pick ticket",
    emailBody: "emailBodyPickTickets",
    reportLink: "PickTicket",
    titleString: "pickTicketTitle",
    nextRefNumberSetString: "pickticketHasNextRefNumberSet",
  },
  PRICE_TIER: { fullString: "pricetier" },
  PRIORITY: { fullString: "priority" },
  PROCESS: {
    fullString: "process",
    shortString: "",
    typeString: "process transaction",
    emailBody: "",
    reportLink: "ProcessTransaction",
    nextRefNumberSetString: "processHasNextRefNumberSet",
  },
  PROCESS_TEMPLATE: { fullString: "processtemplate" },
  PURCHASE: { fullString: "purchase", typeString: "purchase" },
  PURCHASE_ORDER: {
    fullString: "purchaseorder",
    shortString: "PO",
    typeString: "purchase order",
    emailBody: "emailBodyPurchaseOrders",
    reportLink: "PurchaseOrder",
    nextRefNumberSetString: "purchaseorderHasNextRefNumberSet",
  },
  RENTAL: {
    fullString: "rental",
    shortString: "",
    typeString: "rental",
    emailBody: "emailBodyRentals",
    reportLink: "Rental",
    titleString: "rentalTitle",
    nextRefNumberSetString: "rentalHasNextRefNumberSet",
  },
  RENTAL_RETURN: {
    fullString: "rentalreturn",
    shortString: "RentalReturn",
    typeString: "rental return",
    emailBody: "",
    nextRefNumberSetString: "rentalreturnHasNextRefNumberSet",
  },
  REORDER: { fullString: "reorder" },
  RETURN: {
    fullString: "return",
    shortString: "return",
    typeString: "return",
    reportLink: "Return",
    emailBody: "",
    nextRefNumberSetString: "returnHasNextRefNumberSet",
  },
  RETURN_TO_VENDOR: {
    fullString: "returntovendor",
    shortString: "",
    typeString: "return to vendor",
    emailBody: "",
    reportLink: "ReturnToVendor",
    nextRefNumberSetString: "returntovendorHasNextRefNumberSet",
  },
  RMA: {
    fullString: "rma",
    shortString: "RMA",
    typeString: "RMA",
    emailBody: "emailBodyRMAs",
    nextRefNumberSetString: "rmaHasNextRefNumberSet",
  },
  SALES_ORDER: {
    fullString: "salesorder",
    shortString: "SO",
    typeString: "sales order",
    emailBody: "emailBodySalesOrders",
    reportLink: "SalesOrder",
    titleString: "salesOrderTitle",
    nextRefNumberSetString: "salesorderHasNextRefNumberSet",
  },
  SALES_RECEIPT: {
    fullString: "salesreceipt",
    shortString: "SR",
    typeString: "sales receipt",
    emailBody: "emailBodySalesReceipts",
    reportLink: "SalesReceipt",
    nextRefNumberSetString: "salesreceiptHasNextRefNumberSet",
  },
  SALES_REP: { fullString: "salesrep" },
  SALES_TERM: { typeString: "sales term" },
  SERIAL: {
    fullString: "serial",
    shortString: "",
    typeString: "serial item",
    emailBody: "",
  },
  SHIPMENT: {
    fullString: "shipment",
    shortString: "ship",
    typeString: "shipment",
    emailBody: "emailBodyShipments",
    reportLink: "Shipment",
    titleString: "shipmentTitle",
    nextRefNumberSetString: "shipmentHasNextRefNumberSet",
  },
  SHIP_METHOD: { fullString: "shipmethod" },
  SYNC_ERROR: {
    fullString: "syncerror",
    endpointUrl: "sync/error",
  },
  SYNC_ITEM: { fullString: "syncitem" },
  TAG: { fullString: "tag" },
  TERM: { fullString: "terms" },
  TEMPLATE_LIBRARY: { fullString: "templatelibrary" },
  TASK: { fullString: "task", typeString: "task" },
  TAX_CODE: { fullString: "taxcode" },
  TRANSFER: {
    fullString: "transfer",
    shortString: "",
    typeString: "transfer",
    emailBody: "",
    nextRefNumberSetString: "transferHasNextRefNumberSet",
  },
  UOM: { fullString: "uom" },
  UPS_SHIPMENT: { fullString: "upsshipment", typeString: "UPS shipment" },
  UPS_ACCOUNT: { fullString: "upsaccount" },
  USER: { fullString: "user" },
  VENDOR: {
    fullString: "vendor",
    shortString: "",
    typeString: "vendor",
    emailBody: "",
    reportLink: "Vendor",
  },
  VENDOR_CREDIT: { fullString: "vendorcredit", typeString: "vendor credit" },
  VENDOR_ITEM: { fullString: "vendoritem" },
  VENDOR_MESSAGE: { fullString: "vendormessage" },
  WARRANTY: { fullString: "warranty" },
  WORK_CENTER: { fullString: "workcenter" },
  WORK_ORDER: {
    fullString: "workorder",
    shortString: "WO",
    typeString: "work order",
    emailBody: "emailBodyWorkOrders",
    reportLink: "WorkOrder",
    titleString: "workOrderTitle",
    nextRefNumberSetString: "workorderHasNextRefNumberSet",
  },
  WORKER: { fullString: "worker" },
};

// FULL_OBJECT_TYPE_FROM_SHORT will be an object with shortStrings
// as keys and fullStrings as values; so, for example:
// FULL_OBJECT_TYPE_FROM_SHORT["IR"] = "itemreceipt"
const objectTypeArray = Object.entries(OBJECT_TYPES);
const fullFromShortArray = objectTypeArray.map((o) => {
  return [o[1].shortString, o[1].fullString];
});
export const FULL_OBJECT_TYPE_FROM_SHORT =
  Object.fromEntries(fullFromShortArray);

export function getObjectFromFullString(string) {
  return Object.values(OBJECT_TYPES).find(
    ({ fullString }) => fullString === string
  );
}

// Object types that use fromLocation instead of location
export const TO_FROM_LOCATION_OBJECT_TYPES = [
  OBJECT_TYPES.TRANSFER.fullString,
  OBJECT_TYPES.RENTAL.fullString,
  OBJECT_TYPES.RENTAL_RETURN.fullString,
];

// Sales transaction types
export const SALES_TRANSACTION_TYPES = [
  OBJECT_TYPES.INVOICE.fullString,
  OBJECT_TYPES.SALES_ORDER.fullString,
  OBJECT_TYPES.SALES_RECEIPT.fullString,
  OBJECT_TYPES.ESTIMATE.fullString,
];

// Object types that don't multiply BOM component quantities
export const NO_QUANTITY_MULTIPLY_OBJECT_TYPES = [
  OBJECT_TYPES.ADJUSTMENT.fullString,
];

export function getObjectFromTypeString(string) {
  return Object.values(OBJECT_TYPES).find(
    ({ typeString }) => typeString === string
  );
}

export function getObjectFromReportString(string) {
  return Object.values(OBJECT_TYPES).find(
    ({ reportLink }) => reportLink === string
  );
}

export const SOS_BILLING_CURRENCY_CODE = "USD";

export const DEFAULT_BOX_WEIGHT_UNIT = "lb";
export const DEFAULT_BOX_DIMENSION_UNIT = "in";

export const DEFAULT_DECIMALS_UNROUNDED = 5;
export const DEFAULT_DECIMALS_ROUNDED = 2;

export function getSalesDecimalPlaces(roundSalesPrices) {
  return roundSalesPrices
    ? DEFAULT_DECIMALS_ROUNDED
    : DEFAULT_DECIMALS_UNROUNDED;
}

export const PURCHASE_TRANSACTIONS = [
  OBJECT_TYPES.ITEM_RECEIPT.fullString,
  OBJECT_TYPES.PURCHASE_ORDER.fullString,
  OBJECT_TYPES.RETURN_TO_VENDOR.fullString,
];

export const ITEM_TYPES = {
  INVENTORY_ITEM: "Inventory Item",
  NON_INVENTORY_ITEM: "Non-inventory Item",
  CATEGORY: "Category",
  EXPENSE: "Expense",
  ASSEMBLY: "Assembly",
  KIT: "Item Group",
  SERVICE: "Service",
  LABOR: "Labor",
  OVERHEAD: "Overhead",
  OTHER: "Other",
};

export const UNASSIGNED_FILTER_OPTION = {
  id: "-1",
  name: i18n("filter.Unassigned"),
};

export const OVERHEAD_ITEM_TYPE_OPTION = {
  id: ITEM_TYPES.OVERHEAD,
  name: i18n("item.typeOptionOverhead"),
};

export const LABOR_ITEM_TYPE_OPTION = {
  id: ITEM_TYPES.LABOR,
  name: i18n("item.typeOptionLabor"),
};

export const ITEM_TYPE_OPTIONS = [
  { id: ITEM_TYPES.INVENTORY_ITEM, name: i18n("item.typeOptionInventoryItem") },
  {
    id: ITEM_TYPES.NON_INVENTORY_ITEM,
    name: i18n("item.typeOptionNonInventory"),
  },
  { id: ITEM_TYPES.CATEGORY, name: i18n("item.typeOptionCategory") },
  { id: ITEM_TYPES.EXPENSE, name: i18n("item.typeOptionExpense") },
  { id: ITEM_TYPES.ASSEMBLY, name: i18n("item.typeOptionAssembly") },
  { id: ITEM_TYPES.KIT, name: i18n("item.typeOptionItemGroup") },
  { id: ITEM_TYPES.SERVICE, name: i18n("item.typeOptionService") },
  LABOR_ITEM_TYPE_OPTION,
  OVERHEAD_ITEM_TYPE_OPTION,
  { id: ITEM_TYPES.OTHER, name: i18n("item.typeOptionOther") },
];

export const FROM_FORM_ITEM_TYPE_OPTIONS = ITEM_TYPE_OPTIONS.filter(
  ({ id }) => id !== ITEM_TYPES.CATEGORY
);

export const ITEM_TYPES_WITH_INVENTORY_QUANTITIES = [
  ITEM_TYPES.INVENTORY_ITEM,
  ITEM_TYPES.ASSEMBLY,
];
export const ITEM_TYPES_WITH_AVAILABILITY = [
  ITEM_TYPES.KIT,
  ITEM_TYPES.INVENTORY_ITEM,
  ITEM_TYPES.ASSEMBLY,
];

export const ITEM_TYPES_THAT_CAN_BE_ALWAYS_SHIPPABLE = [
  ITEM_TYPES.NON_INVENTORY_ITEM,
  ITEM_TYPES.EXPENSE,
  ITEM_TYPES.KIT,
  ITEM_TYPES.SERVICE,
  ITEM_TYPES.LABOR,
  ITEM_TYPES.OVERHEAD,
  ITEM_TYPES.OTHER,
];

export const ITEM_TYPES_REQUIRING_BINS = [
  ITEM_TYPES.INVENTORY_ITEM,
  ITEM_TYPES.ASSEMBLY,
];

// Tracks which entities track to/from locations
// Tracks which entities map fromBin to bin on lineitems
export const TO_FROM_BIN_OBJECTS = {
  transfer: { fromBin: "fromBin" },
  rental: { fromBin: "bin" },
  rentalreturn: { fromBin: "bin" },
};

export const ITEM_TYPES_WITH_BOM = [ITEM_TYPES.KIT, ITEM_TYPES.ASSEMBLY];

export const CUSTOMER_DATA_RESULTS_WITH_SINGLE_TXN = [
  OBJECT_TYPES.RENTAL_RETURN.fullString,
  OBJECT_TYPES.RETURN.fullString,
];

export const REQUIRE_BIN_SELECTION_FOR_LOT_SERIAL_SELECT = [
  OBJECT_TYPES.ADJUSTMENT.fullString,
  OBJECT_TYPES.BUILD.fullString,
  OBJECT_TYPES.PICK_TICKET.fullString,
  OBJECT_TYPES.PROCESS.fullString,
  OBJECT_TYPES.RENTAL.fullString,
  OBJECT_TYPES.RETURN_TO_VENDOR.fullString,
  OBJECT_TYPES.SHIPMENT.fullString,
  OBJECT_TYPES.TRANSFER.fullString,
];

export const VIEW_COST_REQUIRED = {
  pdf: [
    OBJECT_TYPES.ITEM_RECEIPT.fullString,
    OBJECT_TYPES.PURCHASE_ORDER.fullString,
    OBJECT_TYPES.RETURN.fullString,
  ],
  email: [
    OBJECT_TYPES.ITEM_RECEIPT.fullString,
    OBJECT_TYPES.PURCHASE_ORDER.fullString,
    OBJECT_TYPES.RETURN.fullString,
  ],
  quickView: [
    OBJECT_TYPES.ITEM_RECEIPT.fullString,
    OBJECT_TYPES.PURCHASE_ORDER.fullString,
    OBJECT_TYPES.RETURN.fullString,
  ],
  history: [
    OBJECT_TYPES.VENDOR.fullString,
    OBJECT_TYPES.ITEM.fullString,
    OBJECT_TYPES.SERIAL.fullString,
  ],
};

export const CONCURRENCY_ENABLED_OBJECT_TYPES = [
  OBJECT_TYPES.SALES_ORDER.fullString,
  OBJECT_TYPES.SALES_RECEIPT.fullString,
  OBJECT_TYPES.ESTIMATE.fullString,
  OBJECT_TYPES.INVOICE.fullString,
];

export const SALES_TRANSACTIONS_WITH_LOCATION = [
  OBJECT_TYPES.SALES_ORDER.fullString,
  OBJECT_TYPES.SALES_RECEIPT.fullString,
];

export const DEFAULT_ITEM_TYPE = ITEM_TYPE_OPTIONS[0].id;

export const LB_UNIT = "lb";
export const WEIGHT_UNITS = [LB_UNIT, "oz", "kg", "g"];

export const VOLUME_UNITS = ["cbm"];

export const INCH_UNIT = "in";
export const DIMENSION_UNITS = [INCH_UNIT, "cm"];

// convert *from* the key unit to grams
export const WEIGHT_CONVERSION_FACTORS = {
  g: new Decimal(1),
  oz: new Decimal(28.3495),
  lb: new Decimal(453.592),
  kg: new Decimal(1000),
};

export const NO_COLUMNS = "[none]";

export const NOT_LOGGED_IN_PATHS = [
  "/login",
  "/planselection",
  "/trialsignup",
  "/login/auth",
  "/resetpassword",
  "/forgotpassword",
  "/login/token",
];
export const CONFIGURATION_PATH = "/configuration";
export const HIDDEN_TOP_NAV_PATHS = NOT_LOGGED_IN_PATHS;
export const HIDDEN_LEFT_NAV_PATHS = [
  ...NOT_LOGGED_IN_PATHS,
  CONFIGURATION_PATH,
  "/choosecompany",
  "/expired",
  "/disabled",
];

export const NON_BATCH_DELETE_OBJECT_TYPES = ["location", "warranty"];

export const SUPPORTED_IMAGE_TYPES = ["bmp", "gif", "jpeg", "png"];
export const SUPPORTED_LOGO_IMAGE_TYPES = ["gif", "jpeg", "png"];

export const ATTACHABLE_FILE_TYPES = [
  // from the SOS Inventory back end validation of file extensions
  "image/jpeg",
  "image/gif",
  "image/png",
  "image/bmp",
  "application/vnd.ms-excel",
  "application/msword",
  "application/vnd.ms-powerpoint",
  "application/pdf",
  "application/vnd.ms-project",
  "application/zip",
  "application/vnd.rar",
  "image/vnd.dwg",
  "application/x-msaccess",
  "text/csv",
  "application/x-mspublisher",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template",
  "application/macwriteii",
  "application/rtf",
  "text/plain",
  "application/x-tex",
  "application/vnd.wordperfect",
  "application/vnd.ms-works",
  "image/vnd.adobe.photoshop",
  "application/postscript",
  "application/dxf",
  "application/postscript",
  "application/eps",
  "application/x-eps",
  "image/eps",
  "image/x-eps",
  "image/svg+xml",
  "application/vnd.ms-powerpoint",
  "audio/mpeg",
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-ms-wmv",
  "audio/x-ms-wma",
  "audio/wav",
  "audio/basic",
  "application/lotus123",
  "video/mpeg",
  "application/x-mspublisher",
  // Microsoft Office OpenXML formats
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // .dat Too broad, can't find specific MIME type
  // .dbf, .mdf FoxPro, couldn't find MIME type
  // .wpt Corel WordPerfect; couldn't find MIME type
  // .pst Microsoft items like calendar events, contact, email messages
];

export const IMPORTABLE_EXCEL_TYPES = [
  "text/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

export const ATTACHABLE_MS_WORD_TYPES = [
  "application/msword",
  "application/vnd.ms-word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const FREIGHT_CLASSES = [
  "50",
  "55",
  "60",
  "65",
  "70",
  "77.5",
  "85",
  "92.5",
  "100",
  "110",
  "125",
  "150",
  "175",
  "200",
  "250",
  "300",
  "400",
  "500",
];

export const SEARCH_ABBREVIATIONS = [
  "cust (customer)",
  "adj (adjustment)",
  "build",
  "job",
  "lot",
  "sn (serial number)",
  "ship (shipment)",
  "proc (process transaction)",
  "pick (pick ticket)",
  "xfer (inventory transfer)",
  "est (estimate)",
  "inv (invoice)",
  "item",
  "wo (work order)",
  "po (purchase order)",
  "ir (item receipt)",
  "rent (rental)",
  "ret (return)",
  "rma",
  "rr (rental return)",
  "rtv (return to vendor)",
  "so (sales order)",
  "sr (sales receipt)",
  "vend (vendor)",
];

export const ALL_FILTER_OPTION = { id: 0, name: i18n("filter.All") };

export const ITEM_HISTORY_DEFAULT_FILTERS = {
  direction: "desc",
  maxResults: DEFAULT_LIST_MAX_RESULTS,
  query: "",
  sort: "",
  start: 1,
  fromLocation: ALL_FILTER_OPTION,
  toLocation: ALL_FILTER_OPTION,
  type: ALL_FILTER_OPTION,
  toDate: null,
  fromDate: null,
};

export const NAME_LINKED_FIELD_OBJECTS = [
  OBJECT_TYPES.CLASS.fullString,
  OBJECT_TYPES.CUSTOMER.fullString,
  OBJECT_TYPES.DELETED_ITEM.fullString,
  OBJECT_TYPES.DEPARTMENT.fullString,
  OBJECT_TYPES.ITEM.fullString,
  OBJECT_TYPES.JOB.fullString,
  OBJECT_TYPES.TASK.fullString,
  OBJECT_TYPES.VENDOR.fullString,
];

export const LIST_PAGE_HIDE_ADD_NEW_OBJECTS = [
  OBJECT_TYPES.API_CONNECTION.fullString,
  OBJECT_TYPES.DELETED_ITEM.fullString,
  OBJECT_TYPES.FS_PAYMENT.fullString,
  OBJECT_TYPES.SYNC_ITEM.fullString,
  OBJECT_TYPES.TEMPLATE_LIBRARY.fullString,
  OBJECT_TYPES.AUDIT_EVENT.fullString,
];

export const AUDIT_EVENT_ACTIONS = [
  { id: "Added", name: i18n("filter.Added") },
  { id: "Edited", name: i18n("filter.Edited") },
  { id: "Deleted", name: i18n("filter.Deleted") },
  { id: "Emailed", name: i18n("filter.Emailed") },
  { id: "Synced", name: i18n("filter.Synced") },
];

export const SYNC_ITEM_ACTIONS = [
  { id: "Add", name: i18n("global.Add") },
  { id: "Modify", name: i18n("global.Modify") },
  { id: "Delete", name: i18n("global.Delete") },
];

export const SYNC_ITEM_OBJECT_TYPES = [
  { id: "", name: i18n("filter.All") },
  { id: "Account", name: i18n("global.Account") },
  { id: "Bill", name: i18n("global.Bill") },
  { id: "Class", name: i18n("global.Class") },
  { id: "Credit Memo", name: i18n("objectType.creditmemo.Sentence") },
  { id: "Customer", name: i18n("objectType.customer.Sentence") },
  { id: "Customer Type", name: i18n("customer.CustomerType") },
  { id: "Department", name: i18n("global.Department") },
  { id: "Estimate", name: i18n("objectType.estimate.Sentence") },
  { id: "Invoice", name: i18n("objectType.invoice.Sentence") },
  { id: "Item", name: i18n("global.Item") },
  { id: "Journal Entry", name: i18n("objectType.journalentry.Sentence") },
  { id: "Payment Method", name: i18n("objectType.paymentmethod.Sentence") },
  { id: "Purchase", name: i18n("objectType.purchase.Sentence") },
  { id: "Purchase Order", name: i18n("objectType.purchaseorder.Sentence") },
  { id: "Sales Receipt", name: i18n("objectType.salesreceipt.Sentence") },
  { id: "Sales Term", name: i18n("objectType.salesterm.Sentence") },
  { id: "Vendor", name: i18n("objectType.vendor.Sentence") },
  { id: "Vendor Credit", name: i18n("objectType.vendorcredit.Sentence") },
];

export const SYNC_ITEM_OBJECT_TYPES_GO_TO = [
  OBJECT_TYPES.CLASS.typeString,
  OBJECT_TYPES.DEPARTMENT.typeString,
  OBJECT_TYPES.CUSTOMER.typeString,
  OBJECT_TYPES.CUSTOMER_TYPE.typeString,
  OBJECT_TYPES.ESTIMATE.typeString,
  OBJECT_TYPES.INVOICE.typeString,
  OBJECT_TYPES.ITEM.typeString,
  OBJECT_TYPES.PURCHASE_ORDER.typeString,
  OBJECT_TYPES.SALES_RECEIPT.typeString,
  OBJECT_TYPES.VENDOR.typeString,
];

export const MONTHS = [
  i18n("calendar.january"),
  i18n("calendar.february"),
  i18n("calendar.march"),
  i18n("calendar.april"),
  i18n("calendar.may"),
  i18n("calendar.june"),
  i18n("calendar.july"),
  i18n("calendar.august"),
  i18n("calendar.september"),
  i18n("calendar.october"),
  i18n("calendar.november"),
  i18n("calendar.december"),
];

export const DAYS_OF_WEEK = [
  i18n("calendar.sunday"),
  i18n("calendar.monday"),
  i18n("calendar.tuesday"),
  i18n("calendar.wednesday"),
  i18n("calendar.thursday"),
  i18n("calendar.friday"),
  i18n("calendar.saturday"),
];

export const REQUIRE_TO_EMAIL = [
  OBJECT_TYPES.ADJUSTMENT.fullString,
  OBJECT_TYPES.BUILD.fullString,
  OBJECT_TYPES.PROCESS.fullString,
  OBJECT_TYPES.TRANSFER.fullString,
  OBJECT_TYPES.WORK_ORDER.fullString,
];

export const DEFAULT_EMAIL_TO_BILLING_EMAIL = [
  OBJECT_TYPES.ESTIMATE.fullString,
  OBJECT_TYPES.PURCHASE_ORDER.fullString,
  OBJECT_TYPES.INVOICE.fullString,
  OBJECT_TYPES.SALES_RECEIPT.fullString,
  OBJECT_TYPES.RMA.fullString,
  OBJECT_TYPES.RENTAL.fullString,
  OBJECT_TYPES.PAYMENT.fullString,
];

export const DEFAULT_EMAIL_TO_SHIPPING = [
  OBJECT_TYPES.SALES_ORDER.fullString,
  OBJECT_TYPES.SHIPMENT.fullString,
];

export const STATE_OPTIONS = [
  "AK",
  "AL",
  "AR",
  "AZ",
  "CA",
  "CO",
  "CT",
  "DC",
  "DE",
  "FL",
  "GA",
  "HI",
  "IA",
  "ID",
  "IL",
  "IN",
  "KS",
  "KY",
  "LA",
  "MA",
  "MD",
  "ME",
  "MI",
  "MN",
  "MO",
  "MS",
  "MT",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NM",
  "NV",
  "NY",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  "VT",
  "WA",
  "WI",
  "WV",
  "WY",
];
